import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type SupportedLocale } from './locales.js';

/**
 * Produce a locale-prefixed URL path. The default locale (`en`) is served at
 * the root with no prefix; all other locales are prefixed (`/<locale>/...`).
 *
 * - `localizeUrl('/', 'en')` -> `'/'`
 * - `localizeUrl('/', 'ar')` -> `'/ar/'`
 * - `localizeUrl('/pricing', 'en')` -> `'/pricing'`
 * - `localizeUrl('/pricing', 'fr')` -> `'/fr/pricing'`
 *
 * When `origin` is supplied the result is an absolute URL.
 */
export function localizeUrl(path: string, locale: SupportedLocale, origin?: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const cleaned = normalized === '/' ? '' : normalized;
  const localePart = locale === DEFAULT_LOCALE ? '' : `/${locale}`;
  const url = `${localePart}${cleaned === '' ? '/' : cleaned}`;
  return origin ? `${origin}${url}` : url;
}

export interface HreflangAlternate {
  hreflang: string;
  href: string;
}

export function buildHreflangAlternates(path: string, origin: string): HreflangAlternate[] {
  const alternates: HreflangAlternate[] = SUPPORTED_LOCALES.map((locale) => ({
    hreflang: locale,
    href: localizeUrl(path, locale, origin),
  }));
  alternates.push({ hreflang: 'x-default', href: localizeUrl(path, DEFAULT_LOCALE, origin) });
  return alternates;
}

export function buildCanonical(path: string, locale: SupportedLocale, origin: string): string {
  return localizeUrl(path, locale, origin);
}
